import { useState, type FormEvent } from 'react';
import { OptionChips } from '../../components/OptionChips';
import type { RejectionReason } from '../../types/api';
import { MAX_REGENERATIONS, MAX_REVISIONS } from './reviewLimits';
import {
  SEND_BACK_REASONS,
  chipLabel,
  isRejectionReason,
  pathAction,
  pathCost,
  pathForReason,
  type SendBackPath,
} from './sendBack';

/** Mirrors the backend's bound on every feedback field: 1-1000 characters. */
const FEEDBACK_MAX_LENGTH = 1000;

interface SendBackDialogProps {
  /** Which review step this is: copy selection, or final approval. */
  step: 'copy' | 'visual';
  /**
   * What is on screen at the visual step — a deck of slides, or the one square
   * a generated single came back as. Ignored at the copy step.
   */
  subject?: 'carousel' | 'image';
  regenerationsUsed: number;
  revisionsUsed: number;
  isRegenerationCapped: boolean;
  isRevisionCapped: boolean;
  isSubmitting: boolean;
  error: string | null;
  /** The chip moved: whatever the last path reported no longer applies. */
  onReasonChange: () => void;
  onSubmit: (path: SendBackPath, reason: RejectionReason, feedback: string) => void;
  onCancel: () => void;
}

/**
 * The one place a reviewer says "not this".
 *
 * There used to be two controls here asking the same question — Reject and
 * Revise — and the difference between them was a matter of what the pipeline
 * would have to re-run, which is not something the person looking at a caption
 * knows or should have to. So this asks the question they *can* answer, what
 * kind of problem it is, and routes on the answer: a wording complaint is one
 * revision call, a complaint about the pictures is one design call, and a wrong
 * angle is a real regeneration off the cap of three.
 *
 * The routing is never hidden. The button says which of the three it is about
 * to do, a line under it says what that costs and how much of that budget is
 * already gone, and **Regenerate instead** is always one click away — some
 * complaints are about the angle whatever chip they arrived under, and the
 * reviewer is allowed to know better than the table.
 *
 * A spent budget disables its own path and nothing else: five revisions used
 * does not close regeneration, and a lineage at the cap of three can still be
 * revised. Neither ever blocks approving what is already on screen.
 */
export function SendBackDialog({
  step,
  subject = 'carousel',
  regenerationsUsed,
  revisionsUsed,
  isRegenerationCapped,
  isRevisionCapped,
  isSubmitting,
  error,
  onReasonChange,
  onSubmit,
  onCancel,
}: SendBackDialogProps) {
  const [reason, setReason] = useState<RejectionReason | null>(null);
  const [feedback, setFeedback] = useState('');

  const trimmed = feedback.trim();
  const isValid =
    reason !== null && trimmed.length > 0 && trimmed.length <= FEEDBACK_MAX_LENGTH && !isSubmitting;

  // There is nothing rendered to complain about at copy selection.
  const options = SEND_BACK_REASONS.filter(
    (entry) => step === 'visual' || entry.visualOnly !== true,
  ).map((entry) => ({ value: entry.value, label: chipLabel(entry, subject) }));

  const path: SendBackPath = reason === null ? 'regenerate' : pathForReason(reason);
  const isRegeneration = path === 'regenerate';
  const isPathCapped = isRegeneration ? isRegenerationCapped : isRevisionCapped;
  const used = isRegeneration ? regenerationsUsed : revisionsUsed;

  // Said before the money is spent, not afterwards in a 409.
  const blocked: string[] = [];
  if (isPathCapped) {
    blocked.push(
      isRegeneration
        ? `All ${MAX_REGENERATIONS} tries on this post are used. A wording or picture fix is still available under another answer above, and editing by hand is always free.`
        : `All ${MAX_REVISIONS} revisions on this post are used. Regenerating still works, and editing the caption or the layers by hand is free.`,
    );
  }
  if (!isRegeneration && isRegenerationCapped) {
    blocked.push(
      `Regenerating is closed too: all ${MAX_REGENERATIONS} tries on this post are used.`,
    );
  }

  function handleReason(value: string) {
    if (!isRejectionReason(value)) return;
    setReason(value);
    onReasonChange();
  }

  function send(target: SendBackPath) {
    if (!isValid || reason === null) return;
    if (target === 'regenerate' ? isRegenerationCapped : isRevisionCapped) return;
    onSubmit(target, reason, trimmed);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    send(path);
  }

  return (
    <form className="send-back" onSubmit={handleSubmit} noValidate>
      <h3 className="send-back__title">Send it back</h3>
      <p className="send-back__lede">
        {step === 'copy'
          ? 'What is wrong with this draft? The answer decides what happens next — most problems are one quick edit rather than a whole new post.'
          : `What is wrong with ${subject === 'carousel' ? 'this carousel' : 'this image'}? The answer decides what happens next — the words and the pictures are fixed separately, and neither costs a whole new post.`}
      </p>

      <OptionChips
        legend="What kind of problem is it?"
        options={options}
        selected={reason === null ? [] : [reason]}
        onToggle={handleReason}
        disabled={isSubmitting}
        mode="single"
        name="send-back-reason"
      />

      <label className="field">
        <span className="field__label">What should change</span>
        <textarea
          className="field__input"
          rows={4}
          maxLength={FEEDBACK_MAX_LENGTH}
          value={feedback}
          disabled={isSubmitting}
          onChange={(event) => setFeedback(event.target.value)}
        />
        <span className="field__hint">
          Required — {feedback.length} of {FEEDBACK_MAX_LENGTH} characters. This is the only thing
          we know that we did not know the first time.
        </span>
      </label>

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      {blocked.map((sentence) => (
        <p className="send-back__blocked" key={sentence}>
          {sentence}
        </p>
      ))}

      <p className="send-back__cost">
        {reason === null
          ? 'Pick an answer above and this says what it will cost.'
          : pathCost(path, used, step)}
      </p>

      <div className="send-back__actions">
        <button
          className={isRegeneration ? 'button button--danger' : 'button button--primary'}
          type="submit"
          disabled={!isValid || isPathCapped}
        >
          {isSubmitting ? 'Sending' : pathAction(path, subject)}
        </button>

        {/* The override. A complaint filed as "tone" can still be a request for
            a different idea, and the reviewer is the one who knows. */}
        {isRegeneration ? null : (
          <button
            className="button button--ghost"
            type="button"
            disabled={!isValid || isRegenerationCapped}
            onClick={() => send('regenerate')}
          >
            Regenerate instead
          </button>
        )}

        <button className="button" type="button" disabled={isSubmitting} onClick={onCancel}>
          Keep reviewing
        </button>
      </div>
    </form>
  );
}
